import React, { forwardRef } from 'react'
import { cva } from 'class-variance-authority'
import { VariantProps } from 'class-variance-authority'
import { clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { Modal } from '../Modal'
import { Button } from '../Button'

/**
 * ClickGuard StatusModal — low-level, props-driven modal for status flows.
 *
 * The design system owns the layout (centered card, icon → title → description → actions).
 * The consuming app owns the content (what text, what icons, what buttons).
 */

const titleVariants = cva('cg-font-sans cg-font-semibold cg-text-2xl cg-leading-9', {
  variants: {
    color: {
      default: 'cg-text-navy-500',
      success: 'cg-text-green-500',
      danger: 'cg-text-primary-700',
    },
  },
  defaultVariants: {
    color: 'default',
  },
})

export interface StatusModalAction {
  /** Button label text */
  label: string
  /** Button visual variant */
  variant?: 'primary' | 'blue' | 'purple-gradient' | 'secondary' | 'secondary-white' | 'navy' | 'white' | 'ghost' | 'border-secondary' | 'danger' | 'success' | 'warning' | 'lavender'
  /** Click handler */
  onClick?: () => void
  /** Shows a loading spinner and disables the button */
  loading?: boolean
}

export interface StatusModalProps extends VariantProps<typeof titleVariants> {
  /** Whether the modal is visible */
  open: boolean
  /** Icon or visual element displayed above the title (e.g. Loader, checkmark) */
  icon?: React.ReactNode
  /** Modal heading text */
  title: string
  /** Body content below the title — supports strings or rich ReactNode with links */
  description?: React.ReactNode
  /** Action buttons rendered below the description */
  actions?: StatusModalAction[]
  /** Whether the modal can be dismissed via Escape or overlay click */
  closable?: boolean
  /** Called when the modal requests to close */
  onClose?: () => void
  /** Modal width size */
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** Title color variant */
  color?: 'default' | 'success' | 'danger'
  /** Additional class for the modal content */
  className?: string
}

const StatusModal = forwardRef<HTMLDivElement, StatusModalProps>(
  (
    {
      open,
      icon,
      title,
      description,
      actions,
      closable = true,
      onClose,
      size = 'md',
      color,
      className,
    },
    ref
  ) => {
    return (
      <Modal
        ref={ref}
        open={open}
        onClose={onClose}
        closeOnOverlayClick={closable}
        closeOnEscape={closable}
        size={size}
        className={twMerge(clsx('cg-px-12 cg-py-8', className))}
      >
        <div className="cg-flex cg-flex-col cg-items-center cg-gap-4 cg-text-center">
          {icon && <div className="cg-flex cg-items-center cg-justify-center">{icon}</div>}

          <h2 className={titleVariants({ color })}>{title}</h2>

          {description && (
            <p className="cg-font-sans cg-text-base cg-leading-relaxed cg-text-gray-60">
              {description}
            </p>
          )}

          {actions && actions.length > 0 && (
            <div className="cg-flex cg-flex-col cg-items-center cg-gap-3 cg-w-full cg-mt-2">
              {actions.map((action, i) => (
                <Button
                  key={i}
                  variant={action.variant || 'ghost'}
                  size="sm"
                  onClick={action.onClick}
                  isLoading={action.loading}
                  disabled={action.loading}
                >
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </Modal>
    )
  }
)

StatusModal.displayName = 'StatusModal'

export { StatusModal, titleVariants }
